import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	input,
	OnChanges,
	OnDestroy,
	output,
} from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { formatCmmsLocationsToNode } from '@app/domain/locations/type-translations';
import {
	ICMMSLocation,
	LocationInput,
	LocationTypes,
} from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { LocationAncestryService } from '@app/shared/services/location-ancestry.service.component';
import { LocationSelectorService } from '@app/shared/services/location-selector.service';

@Component({
	selector: 'locations-chip',
	templateUrl: './locations-chip.component.html',
	styleUrl: './locations-chip.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [MatChipsModule, MatIconModule],
})
export class LocationsChipComponent implements OnChanges, OnDestroy {
	private readonly locationAncestryService = inject(LocationAncestryService);
	private readonly locationSelectorService = inject(LocationSelectorService);

	selectedLocations = input<ICMMSLocation[]>([]);
	clickable = input<boolean>(false);
	locationsChanged = output<LocationInput[]>();
	displayedLocations = computed(() =>
		this.selectedLocations()?.map(location =>
			this.locationAncestryService.replaceName(location)
		)
	);

	constructor() {
		effect(() => {
			const locations = this.locationSelectorService.selectedLocations();
			if (!this.selectedLocations() || !locations) {
				return;
			}

			const selectedIds = this.selectedLocations().map(
				location => location.pk || location.locationId
			);

			const noChange =
				locations.every(loc => selectedIds.includes(loc.id)) &&
				selectedIds.every(id => locations.some(loc => loc.id === id));

			if (noChange) {
				return;
			}

			const formattedLocations: LocationInput[] = locations.map(location => ({
				locationId: location.id,
				locationType: (location.contentType.charAt(0).toUpperCase() +
					location.contentType.slice(1)) as LocationTypes,
				name: location.name,
			}));

			this.locationsChanged.emit(formattedLocations);
		});
	}

	ngOnChanges() {
		const formattedLocations = formatCmmsLocationsToNode(this.selectedLocations());
		if (formattedLocations.length > 0 && formattedLocations) {
			this.locationSelectorService.selectedLocations.set(formattedLocations);
		}
	}

	openLocationDialog() {
		if (this.clickable()) {
			this.locationSelectorService.openLocationDialog();
		}
	}

	ngOnDestroy() {
		this.locationSelectorService.selectedLocations.set([]);
	}
}
